/**
 * NMS Product Card Component
 *
 * Handles variant selection, price updates, and cart integration
 * for the minimal product card component.
 *
 * Built following NMS JavaScript standards - extends NMSBaseClass,
 * uses window.neemans namespace, and integrates with existing theme systems.
 */

/**
 * Shared product/family data registry.
 *
 * Product records are keyed by Shopify product ID and deduplicated across
 * card instances (the same product can appear in multiple sections).
 * Color-family records are fetched once per family from
 * sections/nms-family-data.liquid and shared by every card in that family.
 */
const nmsProductRegistry = new Map();
const nmsHandleIndex = new Map();
const nmsFamilyStates = new Map();
const nmsCardInstances = new Set();

/**
 * Card image widths and sizes - must match the image_tag in
 * snippets/nms-product-card.liquid so the preload, the swap and the
 * visible <img> all pick the same srcset candidate (one cached file).
 */
const NMS_CARD_IMAGE_WIDTHS = [200, 300, 400, 500, 600, 700, 800];
const NMS_CARD_IMAGE_SIZES = '(max-width: 699px) 50vw, (max-width: 999px) 33vw, 25vw';

/**
 * Build the card image srcset from a sized Shopify CDN URL, keeping its
 * other query params (e.g. ?v=) so the URLs match the server markup.
 * @param {string} imageUrl - Image URL, typically with width=800
 * @returns {string}
 */
function nmsCardImageSrcset(imageUrl) {
  return NMS_CARD_IMAGE_WIDTHS.map(width => {
    const url = /([?&])width=\d+/.test(imageUrl)
      ? imageUrl.replace(/([?&])width=\d+/, `$1width=${width}`)
      : `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}width=${width}`;
    return `${url} ${width}w`;
  }).join(', ');
}

/**
 * Store a product record in the shared registry (deduped by product ID).
 * @param {Object} record - Canonical product record
 */
function nmsRegistryStore(record) {
  if (!record || !record.id) return;

  const key = String(record.id);

  // Merge with any existing record: newer fields win, but don't let a
  // record that lacks featured_media clobber one that has it (cards emit
  // complete records, fetched family records do too - the merge keeps the
  // richest data regardless of registration order)
  const existing = nmsProductRegistry.get(key);
  const merged = existing ? { ...existing, ...record } : record;

  if (merged.featured_media === undefined || merged.featured_media === null) {
    merged.featured_media = existing?.featured_media ?? null;
  }

  nmsProductRegistry.set(key, merged);

  if (record.handle) {
    nmsHandleIndex.set(record.handle, key);
  }
}

/**
 * Look up a registered product record by its handle.
 * @param {string} handle
 * @returns {Object|null}
 */
function nmsRegistryByHandle(handle) {
  const id = nmsHandleIndex.get(handle);
  return id ? nmsProductRegistry.get(id) || null : null;
}

/**
 * Get (or create) the fetch state for a color family.
 * @param {string} familyKey
 * @param {string[]} handles
 */
function nmsFamilyState(familyKey, handles) {
  let state = nmsFamilyStates.get(familyKey);

  if (!state) {
    state = { handles, promise: null, loaded: false };
    nmsFamilyStates.set(familyKey, state);
  }

  return state;
}

/**
 * Register a resolved product record everywhere consumers can find it.
 * @param {Object} record - Canonical product record
 */
function nmsRegisterResolved(record) {
  nmsRegistryStore(record);

  // Mirror into the legacy global so the size popup's precomputed
  // sizeOption fast path works for siblings too
  window.neemans.productCards = window.neemans.productCards || {};
  window.neemans.productCards[record.id] = record;
}

/**
 * Normalize a native /products/{handle}.js payload into the canonical
 * record shape used for color switching.
 * @param {Object} p - Native product JSON
 * @returns {Object}
 */
function nmsNormalizeNativeProduct(p) {
  const lastMedia = Array.isArray(p.media) && p.media.length ? p.media[p.media.length - 1] : null;
  const imageSrc = lastMedia?.src || p.featured_image || null;
  const imageUrl = imageSrc ? imageSrc + (imageSrc.indexOf('?') === -1 ? '?' : '&') + 'width=800' : null;
  const variants = (p.variants || []).map(v => ({
    id: v.id,
    title: v.title,
    price: v.price,
    compare_at_price: v.compare_at_price ?? null,
    available: v.available,
    option1: v.option1 ?? null,
    option2: v.option2 ?? null,
    option3: v.option3 ?? null,
  }));

  return {
    id: p.id,
    handle: p.handle,
    title: p.title,
    url: p.url,
    available: p.available,
    variants,
    options: (p.options || []).map(o => ({ name: o.name, position: o.position, values: o.values })),
    featured_media: imageUrl
      ? { url: imageUrl, alt: lastMedia?.alt || p.title, preview_image: { url: imageUrl } }
      : null,
    firstAvailableVariant: variants.find(v => v.available)?.id ?? variants[0]?.id ?? null,
    sizeOption: null,
  };
}

/**
 * Fetch the whole color family in one request via the Section Rendering API.
 * @param {string} sourceHandle
 * @returns {Promise<Object[]|null>} records, or null on any failure
 */
async function nmsFetchFamilyViaSection(sourceHandle) {
  try {
    const response = await fetch(
      `/products/${encodeURIComponent(sourceHandle)}?section_id=nms-family-data`,
      { credentials: 'same-origin', headers: { Accept: 'application/json' } }
    );

    if (!response.ok) return null;

    const payload = JSON.parse(await response.text());
    const products = Array.isArray(payload.products) ? payload.products : [];

    return products.length ? products : null;
  } catch (error) {
    return null;
  }
}

/**
 * Fallback: fetch each sibling individually via Shopify's native
 * /products/{handle}.js endpoint - no section file required.
 * @param {string[]} handles
 * @returns {Promise<Object[]>}
 */
async function nmsFetchSiblingsNative(handles) {
  const records = [];

  await Promise.all(
    handles.map(async handle => {
      if (nmsHandleIndex.has(handle)) return; // already registered locally

      try {
        const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });

        if (response.ok) {
          const product = await response.json();
          if (product && product.id) records.push(nmsNormalizeNativeProduct(product));
        }
      } catch (error) {
        // Skip this sibling - click handler falls back to navigation for it
      }
    })
  );

  return records;
}

class NMSProductCard extends window.NMSBaseClass {
  constructor(element) {
    super();

    // Core properties
    this.element = element;
    this.productId = element.dataset.productId;
    this.context = element.dataset.context || 'card';

    // Component data
    this.productData = null;
    this.selectedVariant = null;
    this.selectedOptions = {};
    this.hasSizePopup = false;
    this.hasColorData = false;
    this.colorProducts = null;

    // Color family metadata (sibling handles + fetch state key)
    this.familyKey = null;
    this.familySourceHandle = null;
    this.siblingHandles = [];

    // Latest-selection-wins token for rapid color switching
    this._colorSwitchToken = 0;

    // Use inherited cache from NMSBaseClass instead of creating new Map

    // Size popup instance (created when needed)
    this.sizePopup = null;

    // Initialize global namespace
    window.neemans = window.neemans || {};
    window.neemans.productCard = window.neemans.productCard || {};
    window.neemans.productCard[this.productId] = this;

    // Bind context for event handlers
    this.handleOptionChange = this.handleOptionChange.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleCartSuccess = this.handleCartSuccess.bind(this);
    this.handleCartError = this.handleCartError.bind(this);
    this.handleSizePopupOpen = this.handleSizePopupOpen.bind(this);
    this.handleSizePopupClose = this.handleSizePopupClose.bind(this);
    this.handleColorSwatchClick = this.handleColorSwatchClick.bind(this);
    this._boundSwatchIntent = event => {
      this.#warmSibling(event.currentTarget?.dataset?.productHandle);
    };
  }

  // Initialize component after DOM ready
  init() {
    // Parse data first: #setupRefs depends on hasColorData, so product data
    // must be established before DOM references are discovered.
    try {
      this.#parseProductData();
      this.#setupRefs();
      this.#bindEvents();
      this.#initializeSelectedVariant();

      // Initialize custom swatch carousel if present
      this.#initSwatchCarousel();
    } catch (error) {
      console.error(`[NMS] Product Card init failed:`, error);
      this.#handleInitError(error);
    }
  }

  // Cleanup when component is removed
  destroy() {
    super.destroy();

    document.removeEventListener('variant:add', this.handleCartSuccess);
    document.removeEventListener('cart:error', this.handleCartError);

    // Clean up size popup if it exists
    if (this.sizePopup) {
      this.sizePopup.destroy();
      this.sizePopup = null;
    }

    if (window.neemans.productCard?.[this.productId]) {
      delete window.neemans.productCard[this.productId];
    }

    nmsCardInstances.delete(this);
  }

  /**
   * Parse the canonical inert JSON product data and register it in the
   * shared registry. Also mirrors the record into window.neemans.productCards
   * for backward compatibility (nms-size-popup reads .sizeOption from there).
   * @private
   */
  #parseProductData() {
    const dataScript = this.element.querySelector('[data-nms-product-data]');

    if (!dataScript) {
      throw new Error('Product data script not found');
    }

    try {
      this.productData = JSON.parse(dataScript.textContent);
    } catch (error) {
      throw new Error(`Failed to parse product data: ${error.message}`);
    }

    if (!this.productData || !this.productData.variants) {
      throw new Error('Invalid product data structure');
    }

    // Extract configuration
    this.hasSizePopup = this.productData.size_popup || false;
    this.hasColorData = this.productData.has_color_data || false;
    this.colorData = this.productData.color_data || null;
    this.familySourceHandle = this.productData.sourceHandle || this.productData.handle;
    this.siblingHandles = Array.isArray(this.productData.siblingHandles)
      ? this.productData.siblingHandles
      : [];

    // Backward-compatible global (same record - mutations on color switch
    // propagate just like the old executable-script data did)
    window.neemans.productCards = window.neemans.productCards || {};
    window.neemans.productCards[this.productId] = this.productData;

    nmsRegistryStore(this.productData);

    // Register family state so siblings are fetched once per family
    if (this.hasColorData && this.siblingHandles.length) {
      this.familyKey =
        this.productData.familyKey || this.siblingHandles.slice().sort().join('|');
      nmsFamilyState(this.familyKey, this.siblingHandles);
    }
  }

  /**
   * Setup element references using data-ref attributes
   * @private
   */
  #setupRefs() {
    // Price elements
    this.refs.priceContainer = this.element.querySelector('[data-ref="priceContainer"]');
    this.refs.currentPrice = this.element.querySelector('[data-ref="currentPrice"]');
    this.refs.comparePrice = this.element.querySelector('[data-ref="comparePrice"]');
    this.refs.compareGroup = this.element.querySelector('[data-ref="compareGroup"]');
    this.refs.saleBadge = this.element.querySelector('[data-ref="saleBadge"]');

    // Form elements
    this.refs.variantId = this.element.querySelector('[data-ref="variantId"]');
    this.refs.addButton = this.element.querySelector('[data-ref="addButton"]');
    this.refs.addButtonText = this.element.querySelector('[data-ref="addButtonText"]');
    this.refs.addButtonLoading = this.element.querySelector('[data-ref="addButtonLoading"]');

    // Size popup specific elements
    // Note: this.hasSizePopup is not parsed yet, so query unconditionally
    this.refs.selectSizeButton = this.element.querySelector('[data-ref="selectSizeButton"]');

    // Product elements (always needed for color switching)
    this.refs.productImage = this.element.querySelector('[data-ref="productImage"]');
    this.refs.productTitle = this.element.querySelector('[data-ref="productTitle"]');
    this.refs.productLink = this.element.querySelector('[data-ref="productLink"]');
    this.refs.colorName = this.element.querySelector('[data-ref="colorName"]');

    // Color switching elements (only when color data available)
    if (this.hasColorData) {
      this.refs.colorSwatches = this.element.querySelector('[data-ref="colorSwatches"]');
      this.refs.colorList = this.element.querySelector('[data-ref="colorList"]');
    }

    // Variant selection elements
    this.refs.variantsContainer = this.element.querySelector('[data-ref="variantsContainer"]');
    this.refs.optionSelects = this.element.querySelectorAll('[data-ref^="optionSelect"]');

    // Dynamic option value refs
    for (let i = 1; i <= 3; i++) {
      const optionValue = this.element.querySelector(`[data-ref="optionValue${i}"]`);
      if (optionValue) {
        this.refs[`optionValue${i}`] = optionValue;
      }
    }
  }

  /**
   * Bind event handlers
   * @private
   */
  #bindEvents() {
    // Auto-bind data-nms-click events (scoped to this card only)
    const clickElements = this.element.querySelectorAll('[data-nms-click]');
    for (const element of clickElements) {
      const handlerPath = element.getAttribute('data-nms-click');
      const [methodName, ...args] = handlerPath.split('/').filter(Boolean);

      if (this[methodName] && typeof this[methodName] === 'function') {
        element.addEventListener('click', event => {
          this[methodName].call(this, event, ...args);
        });
      }
    }

    // Option change events (dropdowns)
    this.refs.optionSelects?.forEach(select => {
      select.addEventListener('change', this.handleOptionChange);
    });

    // Option change events (swatches)
    const swatchInputs = this.element.querySelectorAll('.nms-product-card__swatch-input');
    swatchInputs.forEach(input => {
      input.addEventListener('change', this.handleOptionChange);
    });

    // Option change events (size boxes)
    const sizeInputs = this.element.querySelectorAll('.nms-product-card__size-input');
    sizeInputs.forEach(input => {
      input.addEventListener('change', this.handleOptionChange);
    });

    // Form submission - now handled via data-nms-click="/handleAddToCart"

    // Size popup button click - now handled via data-nms-click="/handleSizePopupOpen"

    // Color switching events - now handled via data-nms-click with conflict resolution

    // Warm color-family data on intent (hover / focus / touch) so the actual
    // click hits a warm cache - press-to-release gives ~100ms of head start
    if (this.hasColorData) {
      const intentSwatches = this.element.querySelectorAll('[data-ref="colorSwatch"]');

      for (const swatch of intentSwatches) {
        swatch.addEventListener('pointerenter', this._boundSwatchIntent, { passive: true });
        swatch.addEventListener('focusin', this._boundSwatchIntent, { passive: true });
        swatch.addEventListener('touchstart', this._boundSwatchIntent, { passive: true });
      }
    }

    // Cart events (existing theme integration)
    document.addEventListener('variant:add', this.handleCartSuccess);
    document.addEventListener('cart:error', this.handleCartError);
  }

  /**
   * Initialize with first available variant (Phase 4: Optimized with precomputed data)
   * @private
   */
  #initializeSelectedVariant() {
    // Phase 4: Use precomputed first available variant for better performance
    const precomputedVariantId = this.productData.firstAvailableVariant;

    if (precomputedVariantId) {
      // Find the variant by the precomputed ID
      this.selectedVariant = this.productData.variants.find(v => v.id === precomputedVariantId);
    }

    // Fallback: Use original logic if precomputed data not available or variant not found
    if (!this.selectedVariant) {
      this.selectedVariant = this.productData.variants.find(v => v.available) || this.productData.variants[0];
    }

    if (!this.selectedVariant) {
      console.warn(`[NMS] No variants found for product ${this.productId}`);
      return;
    }

    // Parse selected options from variant
    this.selectedOptions = {
      option1: this.selectedVariant.option1,
      option2: this.selectedVariant.option2,
      option3: this.selectedVariant.option3,
    };

    // Update UI
    this.#updatePrice();
    this.#updateButtonState();
  }

  /**
   * Phase 5: Preload product image for instant color switching
   * @param {Object} productData - Product data with image information
   * @private
   */
  #preloadProductImage(productData) {
    if (!productData?.featured_media?.preview_image?.url) {
      return;
    }

    const imageUrl = productData.featured_media.preview_image.url;

    // Check if image is already preloaded or in browser cache
    const preloadKey = `preloaded-image:${imageUrl}`;

    if (this.cache.has(preloadKey)) {
      return; // Already preloaded
    }

    // Preload with the card's srcset/sizes so the browser fetches the same
    // candidate the visible <img> will use (400-600w on mobile, not 800w)
    const img = new Image();
    img.sizes = this.refs.productImage?.sizes || NMS_CARD_IMAGE_SIZES;
    img.srcset = nmsCardImageSrcset(imageUrl);

    img.onload = () => {
      // Mark as preloaded to avoid duplicate requests
      this.cache.set(preloadKey, true);
    };

    img.onerror = () => {
      // Silently handle preload failures
    };

    // Start preloading
    img.src = imageUrl;
  }

  /**
   * Ensure this card's color-family records are present in the shared
   * registry. Fetches once per family via the nms-family-data section;
   * concurrent callers share the same promise. Resolves false on failure
   * so the caller can fall back to navigation.
   * @returns {Promise<boolean>}
   * @private
   */
  async #ensureFamilyData() {
    if (!this.hasColorData) return false;

    // Derive family state defensively if the JSON payload lacked a key
    let state = this.familyKey ? nmsFamilyStates.get(this.familyKey) : null;

    if (!state && this.siblingHandles.length) {
      this.familyKey = this.siblingHandles.slice().sort().join('|');
      state = nmsFamilyState(this.familyKey, this.siblingHandles);
    }

    if (!state) return false;
    if (state.loaded) return true;
    if (state.promise) return state.promise;

    const sourceHandle = this.familySourceHandle || this.productData.handle;

    state.promise = (async () => {
      // Primary: one request for the whole family via Section Rendering
      let records = await nmsFetchFamilyViaSection(sourceHandle);

      // Fallback: native per-product JSON - works even without the section
      if (!records) {
        records = await nmsFetchSiblingsNative(state.handles);
      }

      for (const record of records) {
        nmsRegisterResolved(record);
      }

      state.loaded = records.length > 0;

      if (!state.loaded) {
        console.warn('[NMS] Family data fetch failed for', sourceHandle);
        state.promise = null; // allow retry on next interaction
      }

      return state.loaded;
    })();

    return state.promise;
  }

  /**
   * Warm the family data (and a specific sibling image) on user intent -
   * swatch hover, focus, or touch - so a subsequent click is instant.
   * @param {string} productHandle
   * @private
   */
  async #warmSibling(productHandle) {
    const ready = await this.#ensureFamilyData();

    if (ready && productHandle) {
      const sibling = nmsRegistryByHandle(productHandle);
      if (sibling) this.#preloadProductImage(sibling);
    }
  }

  /**
   * Handle option selection changes
   * @param {Event} event - Change event from option selector
   */
  handleOptionChange(event) {
    const optionPosition = parseInt(event.target.dataset.optionPosition);
    const optionValue = event.target.value;

    // Update selected options
    this.selectedOptions[`option${optionPosition}`] = optionValue;

    // Find matching variant
    const matchingVariant = this.#findMatchingVariant(this.selectedOptions);

    if (matchingVariant) {
      this.selectedVariant = matchingVariant;
      this.#updateVariantUI();
    } else {
      console.warn(`[NMS] No matching variant found for options:`, this.selectedOptions);
      this.#handleNoMatchingVariant();
    }
  }

  /**
   * Find variant matching current option selections
   * @param {Object} options - Selected option values
   * @returns {Object|null} Matching variant or null
   * @private
   */
  #findMatchingVariant(options) {
    return this.productData.variants.find(variant => {
      return (
        (options.option1 === null || variant.option1 === options.option1) &&
        (options.option2 === null || variant.option2 === options.option2) &&
        (options.option3 === null || variant.option3 === options.option3)
      );
    });
  }

  /**
   * Update UI when variant changes
   * @private
   */
  #updateVariantUI() {
    this.#updatePrice();
    this.#updateButtonState();
    this.#updateVariantId();
    this.#updateOptionLabels();
    this.#updateSizeAvailability();
    this.#updateInventoryFomo(this.selectedVariant?.inventory_quantity);
  }

  /**
   * Update price display
   * @private
   */
  #updatePrice() {
    if (!this.selectedVariant || !this.refs.currentPrice) {
      console.warn('[NMS] Cannot update price - missing variant or price ref');
      return;
    }

    // Format prices using Shopify's money format. Writes are diff-guarded:
    // identical textContent/display assignments still dirty the style tree
    // and, when they differ, become layout shifts - init now writes nothing
    // when the server-rendered markup already matches
    const currentPrice = this.#formatMoney(this.selectedVariant.price);
    if (this.refs.currentPrice.textContent !== currentPrice) {
      this.refs.currentPrice.textContent = currentPrice;
    }

    // Handle compare at price
    if (this.refs.comparePrice && this.selectedVariant.compare_at_price) {
      const comparePrice = this.#formatMoney(this.selectedVariant.compare_at_price);

      if (this.selectedVariant.compare_at_price > this.selectedVariant.price) {
        if (this.refs.comparePrice.textContent !== comparePrice) {
          this.refs.comparePrice.textContent = comparePrice;
        }
        this.refs.comparePrice.style.display = '';
        if (this.refs.compareGroup) this.refs.compareGroup.style.display = '';

        // Calculate and show sale badge. Deal cards render the format driven
        // by the product's custom.nms_savings_format metafield:
        // 'fixed' → "Save ₹X" (default), 'percentage' → "X% OFF".
        if (this.refs.saleBadge) {
          const discountAmount = this.selectedVariant.compare_at_price - this.selectedVariant.price;
          const discountPercentage = Math.round((discountAmount / this.selectedVariant.compare_at_price) * 100);
          let badgeText;
          switch (this.refs.saleBadge.dataset.format) {
            case 'savings': // legacy deal format
            case 'fixed':
              badgeText = `Save ${this.#formatMoney(discountAmount)}`;
              break;
            case 'percentage':
              badgeText = `${discountPercentage}% OFF`;
              break;
            default:
              badgeText = `${discountPercentage}% Off`;
          }
          if (this.refs.saleBadge.textContent !== badgeText) {
            this.refs.saleBadge.textContent = badgeText;
          }
          this.refs.saleBadge.style.display = '';
        }
      } else {
        if (this.refs.comparePrice.style.display !== 'none') {
          this.refs.comparePrice.style.display = 'none';
        }
        // Labeled price layout: hide the "Before" label with the price
        if (this.refs.compareGroup && this.refs.compareGroup.style.display !== 'none') {
          this.refs.compareGroup.style.display = 'none';
        }

        // Hide sale badge
        if (this.refs.saleBadge && this.refs.saleBadge.style.display !== 'none') {
          this.refs.saleBadge.style.display = 'none';
        }
      }
    }
  }

  /**
   * Update add to cart button state based on availability
   * @private
   */
  #updateButtonState() {
    if (!this.refs.addButton || !this.selectedVariant) return;

    const isAvailable = this.selectedVariant.available;

    // Update button enabled/disabled state
    this.refs.addButton.disabled = !isAvailable;

    // Update button text - respect size popup setting
    if (this.refs.addButtonText) {
      let buttonText;

      if (!isAvailable) {
        buttonText = window.themeStrings?.soldOut || 'Sold Out';
      } else if (this.hasSizePopup && this.#hasSizeOption()) {
        buttonText = 'Select Size';
      } else {
        buttonText = this.element.dataset.addLabel || window.themeStrings?.addToCart || 'Add to Cart';
      }

      if (this.refs.addButtonText.textContent !== buttonText) {
        this.refs.addButtonText.textContent = buttonText;
      }
    }

    // Update button classes
    this.refs.addButton.classList.toggle('nms-product-card__add-button--unavailable', !isAvailable);
  }

  /**
   * Update hidden variant ID input
   * @private
   */
  #updateVariantId() {
    if (!this.refs.variantId || !this.selectedVariant) return;

    this.refs.variantId.value = this.selectedVariant.id;
  }

  /**
   * Update option value labels
   * @private
   */
  #updateOptionLabels() {
    Object.keys(this.selectedOptions).forEach((optionKey, index) => {
      const optionNumber = index + 1;
      const optionValueRef = this.refs[`optionValue${optionNumber}`];

      if (optionValueRef && this.selectedOptions[optionKey] && optionValueRef.textContent !== this.selectedOptions[optionKey]) {
        optionValueRef.textContent = this.selectedOptions[optionKey];
      }
    });
  }

  /**
   * Update size box availability based on current selections
   * @private
   */
  #updateSizeAvailability() {
    const sizeInputs = this.element.querySelectorAll('.nms-product-card__size-input');

    sizeInputs.forEach(input => {
      const optionPosition = parseInt(input.dataset.optionPosition);
      const optionValue = input.dataset.optionValue;

      // Check if this size is available with current other option selections
      const testOptions = { ...this.selectedOptions };
      testOptions[`option${optionPosition}`] = optionValue;

      const hasMatchingVariant = this.productData.variants.some(variant => {
        const matches =
          (testOptions.option1 === null || variant.option1 === testOptions.option1) &&
          (testOptions.option2 === null || variant.option2 === testOptions.option2) &&
          (testOptions.option3 === null || variant.option3 === testOptions.option3);
        return matches && variant.available;
      });

      // Update UI based on availability
      const label = input.closest('.nms-product-card__size-label');
      const sizeBox = input.nextElementSibling;

      if (!hasMatchingVariant) {
        input.disabled = true;
        label?.classList.add('nms-product-card__size-label--disabled');
        sizeBox?.classList.add('nms-product-card__size-box--disabled');
        sizeBox?.setAttribute('title', `${optionValue} (Out of Stock)`);
      } else {
        input.disabled = false;
        label?.classList.remove('nms-product-card__size-label--disabled');
        sizeBox?.classList.remove('nms-product-card__size-box--disabled');
        sizeBox?.setAttribute('title', optionValue);
      }
    });
  }

  /**
   * Handle case where no matching variant is found
   * @private
   */
  #handleNoMatchingVariant() {
    // Disable add to cart button
    if (this.refs.addButton) {
      this.refs.addButton.disabled = true;
    }

    // Update button text to show unavailable
    if (this.refs.addButtonText) {
      this.refs.addButtonText.textContent = 'Unavailable';
    }
  }

  /**
   * Handle add to cart button click (auto-bound)
   * @param {Event} event - Click event from button
   */
  handleAddToCart(event) {
    // Don't prevent default - let the theme's form handling work
    // The theme's product-form element will handle the AJAX submission

    // Just add loading state and mark as submitting for our success/error handlers
    this.#setCartButtonLoading(true);
    this.isSubmitting = true;
  }

  /**
   * Handle successful cart addition
   * @param {Event} event - Cart success event
   */
  handleCartSuccess(event) {
    // Only handle success if this card submitted the form
    if (!this.isSubmitting) return;

    // Remove loading state
    this.#setCartButtonLoading(false);

    // Brief success state
    this.#setSuccessState(true);
    setTimeout(() => {
      this.#setSuccessState(false);
      this.isSubmitting = false; // Reset submission flag
    }, 1500);
  }

  /**
   * Handle cart addition error
   * @param {Event} event - Cart error event
   */
  handleCartError(event) {
    // Only handle error if this card submitted the form
    if (!this.isSubmitting) return;

    // Remove loading state
    this.#setCartButtonLoading(false);

    // Show error state
    this.#setErrorState(true);
    setTimeout(() => {
      this.#setErrorState(false);
      this.isSubmitting = false; // Reset submission flag
    }, 3000);
  }

  // Loading state now handled by inherited showLoading/hideLoading methods

  /**
   * Set success state on add to cart button
   * @param {boolean} isSuccess - Success state
   * @private
   */
  #setSuccessState(isSuccess) {
    if (!this.refs.addButton) return;

    this.refs.addButton.classList.toggle('nms-product-card__add-button--success', isSuccess);

    if (isSuccess && this.refs.addButtonText) {
      this.refs.addButtonText.textContent = 'Added!';
    } else {
      this.#updateButtonState(); // Reset to normal state
    }
  }

  /**
   * Set error state on add to cart button
   * @param {boolean} isError - Error state
   * @private
   */
  #setErrorState(isError) {
    if (!this.refs.addButton) return;

    this.refs.addButton.classList.toggle('nms-product-card__add-button--error', isError);

    if (isError && this.refs.addButtonText) {
      // For now, just show generic "Try Again" - could use errorMessage in future
      this.refs.addButtonText.textContent = 'Try Again';
    } else {
      this.#updateButtonState(); // Reset to normal state
    }
  }

  /**
   * Format money using inherited method and remove .00 for whole numbers
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price
   * @private
   */
  #formatMoney(cents) {
    // moneyWithCurrencyFormat mirrors the snippet's `| money_with_currency`
    // so init-time writes produce byte-identical text and diff-guards skip them
    const formattedPrice = this.formatPrice(cents, true);
    // Remove .00 from whole number prices to match Liquid template formatting
    return formattedPrice.replace('.00', '');
  }

  /**
   * Check if product has a size option
   * @returns {boolean}
   * @private
   */
  #hasSizeOption() {
    const sizeLabels = ['size', 'sizes', 'length', 'width', 'shoe size', 'clothing size', 'shirt size'];
    return this.productData.options.some(option => sizeLabels.includes(option.name.toLowerCase()));
  }

  /**
   * Handle size popup open
   * @param {Event} event - Click event from button (auto-bound)
   */
  handleSizePopupOpen(event) {
    if (event) {
      event.stopPropagation(); // Prevent bubbling
    }
    // Create size popup if it doesn't exist
    if (!this.sizePopup && window.NMSSizePopup) {
      this.sizePopup = new NMSSizePopup({
        productId: this.productId,
        productData: this.productData,
        triggerElement: this.refs.selectSizeButton,
        containerElement: document.body, // Append to body to escape modal stacking contexts
        onSelect: selection => this.#handleSizePopupSelection(selection),
        onClose: () => {
          // Hide overlay when popup closes
          const overlay = document.querySelector('.nms-overlay');
          if (overlay) {
            overlay.classList.remove('nms-overlay--size-popup-active');
          }
        },
      });
      this.sizePopup.init();
    }

    // Update product data in case it changed
    if (this.sizePopup) {
      this.sizePopup.updateProductData(this.productData);
      this.sizePopup.show();
    }

    // Show overlay
    const overlay = document.querySelector('.nms-overlay');
    if (overlay) {
      overlay.classList.add('nms-overlay--size-popup-active');
    }
  }

  /**
   * Handle size popup close
   */
  handleSizePopupClose() {
    if (this.sizePopup) {
      this.sizePopup.hide();
      // Note: Overlay is hidden via onClose callback
    }
  }

  /**
   * Handle size selection from popup
   * @param {Object} selection - Selection data from popup
   * @private
   */
  #handleSizePopupSelection(selection) {
    // Update selected options
    this.selectedOptions[`option${selection.optionPosition}`] = selection.size;

    // Find matching variant
    const matchingVariant = this.#findMatchingVariant(this.selectedOptions);

    if (matchingVariant && matchingVariant.available) {
      this.selectedVariant = matchingVariant;
      this.#updateVariantUI();

      // Trigger add to cart after popup closes
      this.isSubmitting = true;
      this.#setCartButtonLoading(true);
      setTimeout(() => {
        this.#addToCartWithSelectedVariant();
      }, 250);
    }
  }

  /**
   * Add to cart with currently selected variant
   * @private
   */
  #addToCartWithSelectedVariant() {
    if (!this.selectedVariant || !this.selectedVariant.available) {
      this.#setCartButtonLoading(false);
      this.isSubmitting = false;
      return;
    }

    // Update hidden variant ID input
    if (this.refs.variantId) {
      this.refs.variantId.value = this.selectedVariant.id;
    }

    // Programmatically submit the product form.  A hidden submit button's
    // .click() does not trigger submission, so use requestSubmit when
    // available and fall back to a synthetic submit event.
    const form = this.refs.addButton?.form;
    if (form) {
      if (form.requestSubmit) {
        form.requestSubmit(this.refs.addButton);
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    }
  }

  /**
   * Get the active cart button (visible select-size button or regular add button)
   * @returns {HTMLElement|null}
   * @private
   */
  #getActiveCartButton() {
    return this.refs.selectSizeButton || this.refs.addButton;
  }

  /**
   * Toggle loading state on the active cart button
   * @param {boolean} isLoading
   * @private
   */
  #setCartButtonLoading(isLoading) {
    const button = this.#getActiveCartButton();
    if (!button) return;

    if (isLoading) {
      button.classList.add('nms-button--loading');
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    } else {
      button.classList.remove('nms-button--loading');
      button.setAttribute('aria-busy', 'false');
      button.disabled = false;
    }
  }

  /**
   * Handle initialization errors gracefully
   * @param {Error} error - Initialization error
   * @private
   */
  #handleInitError(error) {
    // Use inherited error handling
    const message = this.handleError(error, 'Product card initialization failed');

    // Hide variant selection if it failed to initialize
    if (this.refs.variantsContainer) {
      this.refs.variantsContainer.style.display = 'none';
    }

    // Ensure basic functionality still works (direct to product page)
    const productLinks = this.element.querySelectorAll('a[href]');
    productLinks.forEach(link => {
      link.style.display = ''; // Ensure links are still visible
    });
  }

  /**
   * Handle color swatch click for product switching (auto-bound)
   * Sibling records come from the shared family registry - fetched once per
   * family on first intent (or on this click if nothing warmed it). Falls
   * back to plain product navigation when data can't be resolved.
   * @param {Event} event - Click event from color swatch (auto-bound)
   * @param {string} productHandle - Product handle to switch to (from data-nms-click)
   */
  async handleColorSwatchClick(event, productHandle) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation(); // Prevent global color swatches from handling
    }

    const swatch = event.currentTarget;

    // Don't switch if already active
    if (swatch.classList.contains('nms-product-card__color-swatch--active')) {
      return;
    }

    if (!this.hasColorData) {
      console.warn('[NMS] Color switching not available - no color data on this card');
      return;
    }

    const fallbackUrl = swatch.dataset.productUrl || `/products/${productHandle}`;

    // Latest selection wins: bail out of stale async continuations
    const switchToken = ++this._colorSwitchToken;

    try {
      this.#showColorSwitchingLoading(true);

      // Registry-first: the record may already be shared by another card or
      // a previous fetch, even if the family fetch itself later fails
      let productData = nmsRegistryByHandle(productHandle);

      if (!productData) {
        await this.#ensureFamilyData();
        if (switchToken !== this._colorSwitchToken) return;

        productData = nmsRegistryByHandle(productHandle);
      }

      // Last-mile: the family payload may have missed this product (partial
      // data, filtered record) - fetch just this one before ever navigating
      if (!productData) {
        const records = await nmsFetchSiblingsNative([productHandle]);
        if (switchToken !== this._colorSwitchToken) return;

        if (records.length) {
          productData = records[0];
          nmsRegisterResolved(productData);
        }
      }

      if (!productData) {
        // Absolute last resort - data genuinely unavailable
        console.warn('[NMS] Sibling data unavailable, navigating to', fallbackUrl);
        window.location.href = fallbackUrl;
        return;
      }

      await this.#updateProductData(productData);
      if (switchToken !== this._colorSwitchToken) return;

      this.#updateActiveColorSwatch(productHandle);

      // Dispatch color switch success event
      document.dispatchEvent(
        new CustomEvent('nms:color-switched', {
          detail: {
            productId: this.productId,
            newProductHandle: productHandle,
            newProductData: productData,
            component: this,
          },
        })
      );
    } finally {
      if (switchToken === this._colorSwitchToken) {
        this.#showColorSwitchingLoading(false);
      }
    }
  }

  /**
   * Update product data and UI with new product
   * @param {Object} newProductData - New product data
   * @private
   */
  async #updateProductData(newProductData) {
    // Replace (do not mutate) productData: the registry shares these records
    // across cards, and in-place mutation would corrupt the source product's
    // registry entry - making it impossible to ever switch back to it.
    // Card-scoped keys (popup flag, family metadata) are preserved since
    // sibling records don't carry them.
    this.productData = {
      ...newProductData,
      size_popup: this.productData.size_popup,
      has_color_data: this.productData.has_color_data,
      siblingHandles: this.productData.siblingHandles,
      familyKey: this.productData.familyKey,
      sourceHandle: this.productData.sourceHandle,
    };

    // Keep the legacy global pointed at the live record
    window.neemans.productCards[this.productId] = this.productData;

    // Update product image
    if (newProductData.featured_media) {
      // Try multiple selectors to find the product image
      const productImage =
        this.refs.productImage ||
        this.element.querySelector('.nms-product-card__image') ||
        this.element.querySelector('[data-ref="productImage"]') ||
        this.element.querySelector('img');

      if (productImage) {
        // Show loading state without clearing image
        productImage.style.opacity = '0.5';
        productImage.style.filter = 'blur(2px)';

        // IMPORTANT: src/srcset/sizes are built exactly as in
        // #preloadProductImage, so the browser picks the same candidate that
        // was warmed on intent and serves it straight from cache.
        const newSrc = newProductData.featured_media.url;
        const newSrcset = nmsCardImageSrcset(newSrc);
        const imageSizes = productImage.sizes || NMS_CARD_IMAGE_SIZES;

        // Create new image element to preload. The token guard stops an
        // older request that finishes late from overwriting a newer switch.
        const imageToken = this._colorSwitchToken;
        const isStale = () => imageToken !== this._colorSwitchToken;
        const newImage = new Image();
        newImage.sizes = imageSizes;
        newImage.srcset = newSrcset;
        newImage.src = newSrc;

        const applyImage = () => {
          // srcset before src so the visible <img> never fetches the 800w
          // fallback in between
          productImage.srcset = newSrcset;
          productImage.src = newSrc;
          productImage.alt = newProductData.featured_media.alt || newProductData.title;
        };

        // decode() resolves once the chosen candidate is loaded and decoded,
        // so the swap paints in one frame instead of decoding on the main
        // thread right after the src change
        newImage.decode().then(
          () => {
            if (isStale()) return;

            applyImage();

            // Remove loading state
            productImage.style.opacity = '';
            productImage.style.filter = '';
          },
          () => {
            if (isStale()) return;

            // If preload fails, update anyway but keep loading state briefly
            applyImage();

            setTimeout(() => {
              if (isStale()) return;
              productImage.style.opacity = '';
              productImage.style.filter = '';
            }, 500);
          }
        );
      } else {
        console.warn('[NMS] Product image element not found for update');
      }
    }

    // Update product title and links
    if (this.refs.productTitle) {
      const titleLink = this.refs.productTitle.querySelector('a');
      if (titleLink) {
        titleLink.textContent = newProductData.title;
        titleLink.href = `/products/${newProductData.handle}`;
      }
    }

    // Update product image link
    const imageLink = this.element.querySelector('.nms-product-card__image-link');
    if (imageLink) {
      imageLink.href = `/products/${newProductData.handle}`;
    }

    if (this.refs.productLink) {
      this.refs.productLink.href = `/products/${newProductData.handle}`;
    }

    // Update the mobile deal-card "Select color:" label
    if (this.refs.colorName && newProductData.color) {
      this.refs.colorName.textContent = newProductData.color;
    }

    // Carry the sibling's savings-format metafield (deal cards).
    // New-launch price (price--launch) always shows "% OFF", so it keeps its format.
    const isLaunchPrice = this.refs.priceContainer?.classList.contains('nms-product-card__price--launch');
    if (newProductData.savings_format && !isLaunchPrice) {
      this.productData.savings_format = newProductData.savings_format;
      if (this.refs.saleBadge) {
        this.refs.saleBadge.dataset.format = newProductData.savings_format;
      }
    }

    // Reset to first available variant
    this.selectedVariant = newProductData.variants.find(v => v.available) || newProductData.variants[0];

    if (this.selectedVariant) {
      this.selectedOptions = {
        option1: this.selectedVariant.option1,
        option2: this.selectedVariant.option2,
        option3: this.selectedVariant.option3,
      };
    }

    // Update variant selectors
    await this.#updateVariantSelectors(newProductData);

    // Update size popup if it exists
    if (this.hasSizePopup && this.sizePopup) {
      this.sizePopup.updateProductData(newProductData);
    }

    // Update UI
    this.#updateVariantUI();
  }

  /**
   * Update variant selector options with new product data
   * @param {Object} newProductData - New product data
   * @private
   */
  async #updateVariantSelectors(newProductData) {
    // Update dropdown selectors
    this.refs.optionSelects?.forEach((select, index) => {
      const optionPosition = index + 1;
      const option = newProductData.options.find(opt => opt.position === optionPosition);

      if (option) {
        // Clear existing options
        select.innerHTML = '';

        // Add new options
        option.values.forEach(value => {
          const optionElement = document.createElement('option');
          optionElement.value = value;
          optionElement.textContent = value;
          select.appendChild(optionElement);
        });

        // Select first option
        if (option.values.length > 0) {
          select.value = option.values[0];
        }
      }
    });

    // Update size boxes if present
    const sizeInputs = this.element.querySelectorAll('.nms-product-card__size-input');
    if (sizeInputs.length > 0) {
      // For size boxes, we need to rebuild them completely
      // This is a complex operation that would require template regeneration
      // For now, we'll just update availability
      this.#updateSizeAvailability();
    }
  }

  /**
   * Update active state of color swatches
   * @param {string} activeHandle - Handle of active product
   * @private
   */
  #updateActiveColorSwatch(activeHandle) {
    const colorSwatches = this.element.querySelectorAll('[data-ref="colorSwatch"]');

    colorSwatches.forEach(swatch => {
      const swatchHandle = swatch.dataset.productHandle;
      const isActive = swatchHandle === activeHandle;

      swatch.classList.toggle('nms-product-card__color-swatch--active', isActive);
      swatch.disabled = isActive;
    });
  }

  /**
   * Show/hide loading state during color switching
   * @param {boolean} isLoading - Loading state
   * @private
   */
  /**
   * Refresh inventory-mode FOMO rows when the selected variant changes
   * (size pick, option change, colour sibling switch — all funnel through
   * #updateVariantUI). Replaces the {value} token and re-fills the progress
   * bar against the metaobject's start_value baseline.
   * @param {number} count - Selected variant's available inventory
   * @private
   */
  #updateInventoryFomo(count) {
    if (count == null) return;
    const fomos = this.element.querySelectorAll('[data-fomo-type="inventory"]');

    for (const fomo of fomos) {
      const textEl = fomo.querySelector('.nms-flash-offer__text');
      if (textEl) {
        const template = fomo.dataset.fomoTemplate || '{value}';
        const formatted = this.#formatIndianNumber(count);
        // {value}% first (mirrors the Liquid replace order) so a trailing %
        // isn't left behind, then the plain tokens
        textEl.textContent = template
          .replace(/\{\{\s*value\s*\}\}%|\{value\}%/g, formatted)
          .replace(/\{\{\s*value\s*\}\}|\{value\}/g, formatted);
      }

      const max = parseFloat(fomo.dataset.fomoMax || '0');
      const percentage = max > 0 ? Math.min(100, Math.max(0, (count / max) * 100)) : 0;
      const fill = fomo.querySelector('.nms-flash-offer__progress-fill');
      const bar = fomo.querySelector('.nms-flash-offer__progress');
      if (fill) fill.style.setProperty('--flash-offer-progress', `${percentage}%`);
      if (bar) bar.setAttribute('aria-valuenow', String(Math.round(percentage)));
    }
  }

  /**
   * Indian digit grouping (1,240 / 1,24,000) — matches the Liquid
   * formatted_value used for non-percentage FOMO numbers.
   * @private
   */
  #formatIndianNumber(value) {
    const str = String(Math.round(value));
    if (str.length <= 3) return str;
    const head = str.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
    return `${head},${str.slice(-3)}`;
  }

  #showColorSwitchingLoading(isLoading) {
    if (isLoading) {
      // Use inherited loading methods
      if (this.refs.colorSwatches) {
        this.showLoading(this.refs.colorSwatches);
        this.refs.colorSwatches.style.pointerEvents = 'none';
      }

      if (this.refs.productImage) {
        this.refs.productImage.style.opacity = '0.6';
      }
    } else {
      // Use inherited loading methods
      if (this.refs.colorSwatches) {
        this.hideLoading(this.refs.colorSwatches);
        this.refs.colorSwatches.style.pointerEvents = '';
      }

      if (this.refs.productImage) {
        this.refs.productImage.style.opacity = '';
      }
    }
  }

  /**
   * Custom Carousel Navigation Methods
   */

  /**
   * Initialize custom swatch carousel if present
   * @private
   */
  #initSwatchCarousel() {
    // First, refresh refs in case carousel was added after initial scan
    this.#refreshCarouselRefs();

    if (this.refs.swatchContainer && this.refs.prevButton && this.refs.nextButton) {
      // Find the scrollable list
      const scrollList = this.refs.swatchContainer.querySelector('.nms-product-card__color-list--carousel');

      if (scrollList) {
        // Initialize button states based on current scroll position
        this.#updateCarouselButtonStates();

        // Add scroll event listener to update button states during scroll
        // Use passive listener for better performance since we don't preventDefault
        scrollList.addEventListener(
          'scroll',
          () => {
            this.#updateCarouselButtonStates();
          },
          { passive: true }
        );
      }
    }
  }

  /**
   * Calculate carousel scroll dimensions dynamically from DOM
   * @private
   */
  #calculateCarouselDimensions(scrollList) {
    if (!scrollList) {
      return { swatchWidth: 48, gap: 8, scrollAmount: 112 }; // Fallback values
    }

    const swatches = scrollList.querySelectorAll('.nms-product-card__color-swatch');
    if (swatches.length < 2) {
      return { swatchWidth: 48, gap: 8, scrollAmount: 112 }; // Fallback values
    }

    // Get actual swatch width from first swatch
    const firstSwatch = swatches[0];
    const swatchRect = firstSwatch.getBoundingClientRect();
    const swatchWidth = swatchRect.width;

    // Calculate gap by measuring distance between two swatches
    const secondSwatch = swatches[1];
    const secondSwatchRect = secondSwatch.getBoundingClientRect();
    const gap = secondSwatchRect.left - swatchRect.right;

    // Scroll by 2 swatches worth of distance
    const scrollAmount = (swatchWidth + gap) * 2;

    return { swatchWidth, gap, scrollAmount };
  }

  /**
   * Refresh refs specifically for carousel elements
   * @private
   */
  #refreshCarouselRefs() {
    if (!this.element) {
      return;
    }

    // Scan for carousel-specific refs within this component
    const carouselRefs = ['swatchContainer', 'prevButton', 'nextButton', 'swatchCarousel'];

    for (const refName of carouselRefs) {
      const element = this.element.querySelector(`[data-ref="${refName}"]`);
      if (element) {
        this.refs[refName] = element;
      }
    }
  }

  /**
   * Handle previous button click in custom carousel
   * @param {Event} event - Click event
   */
  handleSwatchNavPrev(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Refresh refs if missing (fallback for timing issues)
    if (!this.refs.swatchContainer) {
      this.#refreshCarouselRefs();
    }

    const scrollList = this.refs.swatchContainer?.querySelector('.nms-product-card__color-list--carousel');
    if (scrollList) {
      // Calculate scroll amount dynamically from actual DOM dimensions
      const { scrollAmount } = this.#calculateCarouselDimensions(scrollList);

      scrollList.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth',
      });

      // Update button states after smooth scroll animation completes
      setTimeout(() => this.#updateCarouselButtonStates(), 300);
    }
  }

  /**
   * Handle next button click in custom carousel
   * @param {Event} event - Click event
   */
  handleSwatchNavNext(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Refresh refs if missing (fallback for timing issues)
    if (!this.refs.swatchContainer) {
      this.#refreshCarouselRefs();
    }

    const scrollList = this.refs.swatchContainer?.querySelector('.nms-product-card__color-list--carousel');
    if (scrollList) {
      // Calculate scroll amount dynamically from actual DOM dimensions
      const { scrollAmount } = this.#calculateCarouselDimensions(scrollList);

      scrollList.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      });

      // Update button states after smooth scroll animation completes
      setTimeout(() => this.#updateCarouselButtonStates(), 300);
    }
  }

  /**
   * Update carousel navigation button states based on scroll position
   * @private
   */
  #updateCarouselButtonStates() {
    if (!this.refs.prevButton || !this.refs.nextButton || !this.refs.swatchContainer) {
      return;
    }

    const scrollList = this.refs.swatchContainer.querySelector('.nms-product-card__color-list--carousel');
    if (!scrollList) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = scrollList;
    const threshold = 5; // Small threshold for floating point precision

    // At start if scroll is at or near beginning
    const atStart = scrollLeft <= threshold;

    // At end if scroll is at or near the end
    const atEnd = scrollLeft >= scrollWidth - clientWidth - threshold;

    // Update button states based on scroll position
    this.refs.prevButton.disabled = atStart;
    this.refs.nextButton.disabled = atEnd;
  }
}

/**
 * Initialize a single card element (idempotent).
 * @param {HTMLElement} card
 */
function initSingleProductCard(card) {
  if (card.hasAttribute('data-initialized') || card.hasAttribute('data-initializing')) {
    return;
  }

  card.setAttribute('data-initializing', 'true');
  nmsCardObserver?.unobserve(card);

  try {
    const instance = new NMSProductCard(card);
    instance.init();
    nmsCardInstances.add(instance);
    card.setAttribute('data-initialized', 'true');
  } catch (error) {
    console.error('[NMS] Failed to initialize product card:', error);
  } finally {
    card.removeAttribute('data-initializing');
  }
}

/**
 * Defer initialization of below-the-fold cards until they approach the
 * viewport. 400px of margin means a card is interactive well before the
 * user reaches it; the interaction fallback below covers every other path.
 */
const nmsCardObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              initSingleProductCard(entry.target);
            }
          }
        },
        { rootMargin: '400px 0px' }
      )
    : null;

/**
 * Initialize any card that receives interaction before its deferred init
 * ran. Capture phase guarantees the card's own listeners are attached
 * before the event reaches them - the first click is never lost.
 */
function nmsEnsureCardInitialized(event) {
  const card = event.target?.closest?.('.nms-product-card');

  if (card && !card.hasAttribute('data-initialized')) {
    initSingleProductCard(card);
  }
}

document.addEventListener('pointerdown', nmsEnsureCardInitialized, true);
document.addEventListener('focusin', nmsEnsureCardInitialized, true);
document.addEventListener('click', nmsEnsureCardInitialized, true);

// Initialize NMS Product Cards following standard pattern
function initNMSProductCard(root) {
  // Check for required base class
  if (!window.NMSBaseClass) {
    console.error('[NMS] NMSBaseClass is required for NMSProductCard');
    return;
  }

  // Destroy instances whose elements were removed by AJAX/tab swaps so
  // repeated dynamic updates do not accumulate listeners and timers
  for (const instance of nmsCardInstances) {
    if (!instance.element.isConnected) {
      instance.destroy();
    }
  }

  const scope = root && root.querySelectorAll ? root : document;
  const cards = scope.querySelectorAll('.nms-product-card:not([data-initialized])');

  for (const card of cards) {
    if (nmsCardObserver) {
      nmsCardObserver.observe(card);
    } else {
      initSingleProductCard(card);
    }
  }

  // Handle the case where the scanned root IS a card element
  if (scope.nodeType === 1 && scope.matches?.('.nms-product-card') && !scope.hasAttribute('data-initialized')) {
    if (nmsCardObserver) {
      nmsCardObserver.observe(scope);
    } else {
      initSingleProductCard(scope);
    }
  }
}

// Re-scan whenever dynamically rendered NMS content is inserted
document.addEventListener('nms:content-updated', event => {
  initNMSProductCard(event.target);
});

/**
 * Simple overlay click handler - closes size popup when overlay is clicked
 */
function initOverlayClickHandler() {
  const overlay = document.querySelector('.nms-overlay');
  if (!overlay) return;

  overlay.addEventListener('click', event => {
    event.preventDefault();
    event.stopPropagation();

    // Only handle clicks when size popup overlay is active (not mega menu overlay)
    if (!overlay.classList.contains('nms-overlay--size-popup-active')) {
      return;
    }

    const openPopup = document.querySelector('.nms-size-popup--show');
    if (openPopup) {
      // Popups are appended to document.body, so find the owning card
      // instance directly instead of walking the DOM from the popup
      for (const instance of nmsCardInstances) {
        if (instance.sizePopup?.element === openPopup) {
          instance.handleSizePopupClose();
          break;
        }
      }
    }
  });
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initNMSProductCard();
    initOverlayClickHandler();
  });
} else {
  initNMSProductCard();
  initOverlayClickHandler();
}

// Export for manual initialization if needed
window.NMSProductCard = NMSProductCard;
window.initNMSProductCard = initNMSProductCard;
